/**
 * Modal component for displaying professionals list
 */

import type { WidgetConfig, ProfessionalSummary } from '../types';
import {
  formatWeekday,
  formatShortMDY,
  getPositionAcronym,
  buildProfileUrl,
  formatProfessionalName,
  escapeHtml,
  shouldOpenModalFromQuery,
  getInitialQueryParams,
  shouldShowPartnerBranding,
} from '../utils';
import { CDN_ASSETS } from '../config';

const REDIRECT_MODAL_OPENED_KEY = 'ondiem_redirect_modal_opened';

function hasAlreadyOpenedModalFromRedirect(): boolean {
  try {
    return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(REDIRECT_MODAL_OPENED_KEY) === '1';
  } catch {
    return false;
  }
}

function setRedirectModalOpened(): void {
  try {
    sessionStorage?.setItem(REDIRECT_MODAL_OPENED_KEY, '1');
  } catch {
    // ignore
  }
}

const MODAL_LOADING_HTML = `
  <div class="pro-availability_modal-loading" aria-busy="true">
    <div class="spinner" aria-hidden="true"></div>
    <p class="pro-availability_modal-loading-text">Loading professionals…</p>
  </div>
`;

export class AvailabilityModal {
  private container: HTMLElement;
  private config: WidgetConfig;
  private prosByDate: Record<string, ProfessionalSummary[]> = {};
  private escapeHandlerBound: boolean = false;
  /** Tracks the modal DOM element that already has click listeners attached */
  private modalListenersAttachedTo: HTMLElement | null = null;
  /** Once we've opened from redirect hash, ignore hash on subsequent filter changes */
  private redirectHashConsumed: boolean = false;
  /** Date currently shown in the modal (so we can refresh content when data arrives) */
  private openDate: string | null = null;
  /** Date the open event was emitted for, so a spinner-then-content open only counts once. */
  private emittedOpenFor: string | null = null;

  constructor(container: HTMLElement, config: WidgetConfig) {
    this.container = container;
    this.config = config;
  }

  /**
   * Updates the professionals data. If the modal is open and was showing a loading state
   * for this date, refreshes the modal content (replaces spinner with list).
   */
  updateProsByDate(prosByDate: Record<string, ProfessionalSummary[]>): void {
    this.prosByDate = prosByDate;
    if (this.openDate && this.prosByDate[this.openDate] !== undefined) {
      this.updateModalContent(this.openDate);
    }
  }

  /**
   * Updates the config reference
   */
  updateConfig(config: WidgetConfig): void {
    this.config = config;
  }

  /**
   * Builds modal HTML structure (matches Webflow implementation)
   */
  buildHTML(): string {
    return `
      <div id="pros-modal" aria-hidden="true" class="pro-availability_modal">
        <div data-action="close" class="pro-availability_modal-overlay"></div>
        <div aria-labelledby="pros-modal-title" role="dialog" aria-modal="true" class="pro-availability_modal-dialog">
          <a aria-label="close" data-action="close" href="#" class="pro-availability_modal-close w-inline-block">
            <img src="${CDN_ASSETS.CLOSE_ICON}" loading="lazy" alt="">
          </a>
          <div class="pro-availability_modal-header">
            <h3 id="pros-modal-title" class="pro-availability_modal-title">Professionals ready to work:</h3>
            <div class="pro-availability_modal-date-wrapper">
              <img src="${CDN_ASSETS.CALENDAR_ICON}" loading="lazy" alt="">
              <div id="pros-modal-date" class="pro-availability_modal-date"></div>
            </div>
          </div>
          <p id="pros-modal-count" class="pro-availability_modal-count text-align-center text-weight-medium"></p>
          <div id="pros-modal-list" class="pro-availability_modal-list"></div>
        </div>
      </div>
    `;
  }

  /**
   * Ensures modal is appended to document.body for proper z-index stacking
   * This is necessary when the widget is embedded in pages with complex stacking contexts
   */
  ensureInBody(): void {
    // Check if modal already exists in body
    let wrapper = document.body.querySelector('#pros-modal-wrapper') as HTMLElement;
    
    if (!wrapper) {
      // Create wrapper div with widget class to maintain CSS scoping.
      // The modal lives outside the widget root, so it needs is-partner applied
      // independently — otherwise its badges and links keep the base styling
      // while the cards behind it use the partner palette.
      wrapper = document.createElement('div');
      wrapper.id = 'pros-modal-wrapper';
      wrapper.className = shouldShowPartnerBranding(this.config.branding)
        ? 'ondiem-pro-availability-widget is-partner'
        : 'ondiem-pro-availability-widget';
      
      // Create modal in wrapper
      const modalHTML = this.buildHTML();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = modalHTML;
      const modal = tempDiv.firstElementChild as HTMLElement;
      
      if (modal) {
        wrapper.appendChild(modal);
        document.body.appendChild(wrapper);
      }
    }
  }

  /**
   * Returns the modal DOM element (body wrapper, then body, then container).
   * @param openOnly - if true, only return the element when it has class is-open
   */
  private getModalElement(openOnly = false): HTMLElement | null {
    const suffix = openOnly ? '.is-open' : '';
    return (
      document.body.querySelector(`#pros-modal-wrapper #pros-modal${suffix}`) as HTMLElement ||
      document.body.querySelector(`#pros-modal${suffix}`) as HTMLElement ||
      this.container.querySelector(`#pros-modal${suffix}`) as HTMLElement ||
      null
    );
  }

  /**
   * Attaches event listeners for modal.
   * The modal element lives in document.body and persists across widget renders,
   * so listeners must only be added once to avoid accumulation.
   */
  attachEventListeners(): void {
    const modal = this.getModalElement();
    if (!modal) return;

    // Skip if we already attached listeners to this modal DOM element
    if (this.modalListenersAttachedTo === modal) return;
    this.modalListenersAttachedTo = modal;

    const overlay = modal.querySelector('.pro-availability_modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close('overlay'));
    }

    modal.querySelectorAll('[data-action="close"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.close('button');
      });
    });

    // Profile tiles are re-rendered into #pros-modal-list on every open, so the listener
    // is delegated from the modal element, which persists across renders.
    modal.addEventListener('click', (e) => {
      const link = (e.target as Element | null)?.closest?.('.js-profile-link');
      if (link) this.emitProfileClick(link);
    });

    if (!this.escapeHandlerBound) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.getModalElement(true)) {
          this.close('escape');
        }
      });
      this.escapeHandlerBound = true;
    }
  }

  /**
   * Checks query params on page load to auto-open modal if needed (e.g. after redirect).
   * Only runs once per tab (survives refresh via sessionStorage); filter changes don't re-open.
   */
  async checkQueryParamsForModal(): Promise<void> {
    if (this.redirectHashConsumed) return;
    if (hasAlreadyOpenedModalFromRedirect()) return;
    const params = shouldOpenModalFromQuery();
    if (!params.open || !params.date) return;

    // Check if we already have data for this date
    const hasData = this.prosByDate[params.date] !== undefined;

    // Update filters based on query params
    // When coming from redirect (openAvailability=1), always use query params
    // If zip/position are not in query params, clear them to show all pros (default behavior)
    let needsReload = false;
    
    // Use initial query params (as when page loaded) so we don't lose them if host stripped the URL
    const queryParams = getInitialQueryParams();
    const hasZipParam = queryParams.has('zip');
    const hasPositionParam = queryParams.has('position');
    
    // Update zip: use query param value if provided, otherwise clear to null to show all pros
    const newZip = hasZipParam ? (params.zip || null) : null;
    if (newZip !== this.config.zipCode) {
      const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement;
      if (zipInput) {
        zipInput.value = newZip || '';
        this.config.zipCode = newZip;
        needsReload = true;
      }
    }

    // Update position: use query param value if provided, otherwise clear to null to show all pros
    const newPosition = hasPositionParam ? (params.position || null) : null;
    if (newPosition !== this.config.position) {
      const positionSelect = this.container.querySelector('#ondiem-position-select') as HTMLSelectElement;
      if (positionSelect) {
        positionSelect.value = newPosition || '';
        this.config.position = newPosition;
        needsReload = true;
      }
    }

    if (needsReload) {
      this.container.dispatchEvent(
        new CustomEvent('ondiem:filter-change', {
          detail: { zip: newZip, position: newPosition },
        })
      );
      this.redirectHashConsumed = true;
      setRedirectModalOpened();
      this.open(params.date!); // Opens with spinner; updateProsByDate will refresh when data loads
    } else if (hasData) {
      this.redirectHashConsumed = true;
      setRedirectModalOpened();
      setTimeout(() => this.open(params.date!), 100);
    } else {
      this.redirectHashConsumed = true;
      setRedirectModalOpened();
      this.open(params.date!); // Opens with spinner; updateProsByDate will refresh when data loads
    }
  }

  /**
   * Updates only the count and list in the modal (used when data arrives after opening with spinner).
   */
  private updateModalContent(date: string): void {
    const modal = this.getModalElement();
    if (!modal) return;
    const modalCount = modal.querySelector('#pros-modal-count') as HTMLElement;
    const modalList = modal.querySelector('#pros-modal-list') as HTMLElement;
    if (!modalCount || !modalList) return;

    const pros = this.prosByDate[date] || [];
    if (pros.length > 0) {
      modalCount.innerHTML = `<span class="text-weight-bold">${pros.length}</span> professional${pros.length === 1 ? '' : 's'} to choose from:`;
    } else {
      modalCount.textContent = 'All professionals shown above';
    }
    modalList.innerHTML = this.buildProTilesHTML(date, pros, null);
  }

  /**
   * Builds HTML for the list of professional tiles (optionally with city/state).
   */
  private buildProTilesHTML(date: string, pros: { first_name?: string; last_initial?: string; user_uuid: string; position_type?: string }[], cityState: string | null): string {
    return pros.map((pro) => {
      const name = formatProfessionalName(pro);
      const href = buildProfileUrl(pro.user_uuid, this.config.utmContent);
      const positionAcr = getPositionAcronym(pro.position_type || '');
      const badge = positionAcr
        ? `<div class="pro-availability_position-tab" title="${escapeHtml(positionAcr.label)}" aria-label="${escapeHtml(positionAcr.label)}">${escapeHtml(positionAcr.abbr)}</div>`
        : '';
      const location = cityState
        ? `<p class="pro-availability_modal-tile-content-location">${escapeHtml(cityState)}</p>`
        : '';
      return `
        <a class="js-profile-link" href="${escapeHtml(href)}" target="_blank" rel="noopener" data-user-uuid="${escapeHtml(pro.user_uuid)}" data-pos="${escapeHtml(pro.position_type || '')}" data-date="${escapeHtml(date)}">
          <div class="pro-availability_modal-tile">
            <div class="pro-availability_modal-tile-content">
              <p>${escapeHtml(name)}</p>${location}
            </div>
            ${badge}
          </div>
        </a>
      `;
    }).join('');
  }

  /**
   * Emits the modal open event. Called from both open() paths — the spinner path that
   * returns early and the loaded path — because a redirect arrival always opens on the
   * spinner, and firing only on the loaded path lost every partner-referred open.
   * resultsCount is null when the data hasn't arrived yet.
   */
  private emitOpen(date: string, resultsCount: number | null): void {
    if (this.emittedOpenFor === date) return;
    this.emittedOpenFor = date;
    try {
      const analytics = (window as any).odAnalytics?.component?.('pro_availability', {
        instance: this.config.instance || 'default',
        version: '1.0.0',
      });
      analytics?.open?.({
        target: 'modal',
        date,
        results_count: resultsCount,
        position_type: this.config.position || '(any)',
        zip: this.config.zipCode || '(none)',
      });
    } catch {
      // Analytics errors should not prevent the modal from opening
    }
  }

  /**
   * Emits the profile-click event when a pro tile is clicked. The tile is a plain
   * target="_blank" link, so this fires alongside navigation rather than replacing it —
   * nothing is prevented and the current page never unloads.
   */
  private emitProfileClick(link: Element): void {
    try {
      const analytics = (window as any).odAnalytics?.component?.('pro_availability', {
        instance: this.config.instance || 'default',
        version: '1.0.0',
      });
      analytics?.event?.('profile_click', {
        target: 'profile',
        user_uuid: link.getAttribute('data-user-uuid') || '',
        position_type: link.getAttribute('data-pos') || '(any)',
        date: link.getAttribute('data-date') || '',
        zip: this.config.zipCode || '(none)',
      });
    } catch {
      // Analytics errors should not prevent the profile link from opening
    }
  }

  /**
   * Opens the modal with professionals for the given date.
   * If data for that date is still loading, shows a spinner until updateProsByDate receives it.
   */
  async open(date: string): Promise<void> {
    try {
      this.ensureInBody();
      const modal = this.getModalElement();
      if (!modal) return;

      const modalDateEl = modal.querySelector('#pros-modal-date') as HTMLElement;
      const modalCount = modal.querySelector('#pros-modal-count') as HTMLElement;
      const modalList = modal.querySelector('#pros-modal-list') as HTMLElement;
      if (!modalDateEl || !modalCount || !modalList) return;

      this.openDate = date;
      const pros = this.prosByDate[date];
      const isLoading = pros === undefined;

      const weekday = formatWeekday(date, 'long');
      const dateFormatted = formatShortMDY(date);
      modalDateEl.textContent = `${weekday} ${dateFormatted}`;

      if (isLoading) {
        modalCount.textContent = '';
        modalList.innerHTML = MODAL_LOADING_HTML;
        modal.setAttribute('aria-hidden', 'false');
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
        void modal.offsetHeight;
        this.emitOpen(date, null);
        return;
      }

      if (pros.length > 0) {
        modalCount.innerHTML = `<span class="text-weight-bold">${pros.length}</span> professional${pros.length === 1 ? '' : 's'} to choose from:`;
      } else {
        modalCount.textContent = 'All professionals shown above';
      }

      // Emitted before the awaited city lookup below: lookupCityState() is throttled to
      // one call per 15s, and anything that throws in that block lands in the catch,
      // which opens the modal without reaching the old analytics call at the end.
      this.emitOpen(date, pros.length);

      let cityState: string | null = null;
      try {
        const { lookupCityState } = await import('../api');
        const zipInput = this.container.querySelector('#ondiem-zip-input') as HTMLInputElement;
        const zip = zipInput?.value?.trim() || '';
        if (zip) {
          try {
            cityState = await lookupCityState(zip);
          } catch {
            // Location is optional
          }
        }
      } catch {
        // Continue without location lookup
      }

      modalList.innerHTML = this.buildProTilesHTML(date, pros, cityState);

      modal.setAttribute('aria-hidden', 'false');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      void modal.offsetHeight;

    } catch (error) {
      console.error('Error opening modal:', error);
      const modal = this.getModalElement();
      if (modal) {
        modal.setAttribute('aria-hidden', 'false');
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    }
  }

  /**
   * Closes the modal
   */
  close(reason: string = 'unknown'): void {
    this.openDate = null;
    const modal = this.getModalElement();
    if (!modal) return;

    // The overlay listener fires on any overlay click, and close() is also called on
    // paths where nothing is open. Without this the close event outnumbered open ~3:1.
    const wasOpen = modal.classList.contains('is-open');
    this.emittedOpenFor = null;

    modal.classList.add('is-closing');
    modal.classList.remove('is-open');
    
    const handleTransitionEnd = () => {
      modal.classList.remove('is-closing');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      modal.removeEventListener('transitionend', handleTransitionEnd);
    };

    modal.addEventListener('transitionend', handleTransitionEnd, { once: true });
    
    // Fallback timeout in case transition doesn't fire
    setTimeout(handleTransitionEnd, 400);

    // Analytics (wrap in try-catch to prevent failures from blocking modal close)
    if (!wasOpen) return;
    try {
      const analytics = (window as any).odAnalytics?.component?.('pro_availability', {
        instance: this.config.instance || 'default',
        version: '1.0.0',
      });
      if (analytics?.close) {
        analytics.close({ target: 'modal', reason });
      }
    } catch {
      // Analytics errors should not prevent modal from closing
    }
  }
}
